import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from "framer-motion";
import {
  Menu, X, Zap, Shield, Brain, DollarSign, Clock, Users,
  ChevronRight, Check, ArrowRight, Bell, BarChart3, Settings,
  LogOut, Home, Mail, AlertTriangle, TrendingDown, Sparkles,
  XCircle, Crown
} from "lucide-react";

import { API } from "./config/api";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { Navigation } from "./components/Navigation";
import { PriceComparison } from "./components/PriceComparison";
import { ParallaxSection } from "./components/animation/ParallaxSection";
import { FloatingElement } from "./components/animation/FloatingElement";

// Apple-style Device Showcase Component
const DeviceShowcase = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.8, 1, 1, 0.9]);
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [15, 0, -10]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const [currentLine, setCurrentLine] = useState(0);
  const terminalLines = [
    { type: "input", text: "$ pagerless analyze --alert-id=ALT-4521" },
    { type: "output", text: "🔍 Analyzing alert..." },
    { type: "output", text: "" },
    { type: "header", text: "━━━ ROOT CAUSE ANALYSIS ━━━" },
    { type: "label", text: "Service:", value: "payment-api" },
    { type: "label", text: "Issue:", value: "Connection pool exhausted" },
    { type: "label", text: "Impact:", value: "23% of requests failing" },
    { type: "output", text: "" },
    { type: "header", text: "━━━ AI RECOMMENDATION ━━━" },
    { type: "success", text: "✓ Increase DB pool size: 10 → 25" },
    { type: "success", text: "✓ Add connection timeout: 30s" },
    { type: "success", text: "✓ Enable circuit breaker" },
    { type: "output", text: "" },
    { type: "info", text: "⚡ Auto-fix available. Run: pagerless fix --apply" },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentLine(prev => (prev < terminalLines.length ? prev + 1 : prev));
    }, 300);
    return () => clearInterval(timer);
  }, [terminalLines.length]);

  return (
    <section ref={containerRef} className="py-32 md:py-48 bg-[#030303] overflow-hidden relative">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-blue-500/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
            See it in action
          </h2>
          <p className="text-lg text-gray-400">
            Watch AI diagnose and fix issues in real-time
          </p>
        </motion.div>

        {/* Floating Device */}
        <motion.div
          style={{ y, scale, rotateX, opacity, perspective: 1200 }}
          className="relative mx-auto max-w-4xl"
        >
          {/* Device Frame */}
          <div className="relative rounded-2xl bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] p-1 shadow-2xl shadow-black/50">
            {/* Browser Chrome */}
            <div className="rounded-t-xl bg-[#1a1a1a] px-4 py-3 flex items-center gap-3">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
                <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 py-1 rounded-md bg-[#0d0d0d] text-xs text-gray-500 font-mono">
                  pagerless.in/dashboard
                </div>
              </div>
              <div className="w-16"></div>
            </div>

            {/* Terminal Content */}
            <div className="rounded-b-xl bg-[#0a0a0a] p-6 min-h-[400px] font-mono text-sm overflow-hidden">
              <div className="space-y-2">
                {terminalLines.slice(0, currentLine).map((line, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`
                      ${line.type === 'input' ? 'text-green-400' : ''}
                      ${line.type === 'output' ? 'text-gray-500' : ''}
                      ${line.type === 'header' ? 'text-blue-400 font-bold mt-2' : ''}
                      ${line.type === 'success' ? 'text-emerald-400' : ''}
                      ${line.type === 'info' ? 'text-yellow-400' : ''}
                      ${line.type === 'label' ? 'text-gray-400' : ''}
                    `}
                  >
                    {line.type === 'label' ? (
                      <span>
                        <span className="text-gray-500">{line.text}</span>{' '}
                        <span className="text-white">{line.value}</span>
                      </span>
                    ) : (
                      line.text
                    )}
                  </motion.div>
                ))}
                {currentLine < terminalLines.length && (
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                    className="inline-block w-2 h-4 bg-green-400"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Reflection */}
          <div className="absolute -bottom-20 left-0 right-0 h-20 bg-gradient-to-b from-white/5 to-transparent rounded-b-2xl blur-sm opacity-30 transform scale-y-[-1]"></div>
        </motion.div>
      </div>
    </section>
  );
};

// Integrations Section
const IntegrationsSection = () => {
  const integrations = [
    {
      name: "Slack",
      description: "Get alerts in channels, acknowledge from threads",
      color: "#4A154B",
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
          <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
        </svg>
      )
    },
    {
      name: "Microsoft Teams",
      description: "Native Teams app with adaptive cards",
      color: "#5059C9",
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.19 8.77h-3.38V6.73c0-.79.64-1.43 1.43-1.43h.52c.79 0 1.43.64 1.43 1.43v2.04zm-1.43 9.53c2.57 0 4.65-2.08 4.65-4.65v-3.88c0-.79-.64-1.43-1.43-1.43h-3.22v5.31c0 .53-.11 1.04-.3 1.5-.48 1.14-1.51 2.01-2.76 2.28.56.55 1.32.88 2.16.88h.9zm-5.16-6.53c1.53 0 2.77-1.24 2.77-2.77s-1.24-2.77-2.77-2.77-2.77 1.24-2.77 2.77 1.24 2.77 2.77 2.77zm3.1 1.1H9.5c-.79 0-1.43.64-1.43 1.43v3.88c0 2.57 2.08 4.65 4.65 4.65h.9c2.57 0 4.65-2.08 4.65-4.65v-3.88c0-.79-.64-1.43-1.43-1.43h-.04z"/>
        </svg>
      )
    },
    {
      name: "Discord",
      description: "Bot for gaming & dev communities",
      color: "#5865F2",
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
      )
    },
    {
      name: "Telegram",
      description: "Instant alerts via Telegram bot",
      color: "#0088cc",
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      )
    },
    {
      name: "Email",
      description: "Email alerts with full context",
      color: "#EA4335",
      icon: (
        <Mail className="w-8 h-8" />
      )
    },
    {
      name: "Webhooks",
      description: "Connect any tool via webhooks",
      color: "#FF6B6B",
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
        </svg>
      )
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden">
      {/* Background grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            50+ Integrations
          </span>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Works where you work
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Get alerts in Slack, Teams, Discord, or any tool your team uses.
            One-click setup, zero configuration headaches.
          </p>
        </motion.div>

        {/* Integration Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
          {integrations.map((integration, i) => (
            <motion.div
              key={integration.name}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              whileHover={{
                scale: 1.05,
                y: -5,
                transition: { duration: 0.2 }
              }}
              className="group relative p-6 rounded-2xl bg-[#151515] border border-white/5 hover:border-white/20 transition-colors cursor-pointer overflow-hidden"
            >
              {/* Hover glow */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: `radial-gradient(circle at center, ${integration.color}15 0%, transparent 70%)`
                }}
              />

              <div className="relative z-10">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${integration.color}20`, color: integration.color }}
                >
                  {integration.icon}
                </div>
                <h3 className="text-white font-semibold mb-1 group-hover:text-blue-400 transition-colors">
                  {integration.name}
                </h3>
                <p className="text-gray-500 text-sm">
                  {integration.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Integration Flow Visualization */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="relative rounded-2xl bg-[#050505] border border-white/10 p-8 md:p-12 overflow-hidden"
        >
          {/* Animated connection lines background */}
          <div className="absolute inset-0 opacity-20">
            <svg className="w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="none">
              <motion.path
                d="M0,100 Q200,50 400,100 T800,100"
                fill="none"
                stroke="url(#gradient1)"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2, ease: "easeInOut" }}
              />
              <defs>
                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity="0" />
                  <stop offset="50%" stopColor="#2563EB" stopOpacity="1" />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Source */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mb-3">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <span className="text-white font-medium">Your Alert</span>
              <span className="text-gray-500 text-sm">Any source</span>
            </div>

            {/* Arrow */}
            <motion.div
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="hidden md:block"
            >
              <ArrowRight className="w-8 h-8 text-blue-500" />
            </motion.div>

            {/* Pagerless */}
            <div className="flex flex-col items-center text-center">
              <motion.div
                className="w-20 h-20 rounded-2xl bg-blue-600 flex items-center justify-center mb-3 shadow-lg shadow-blue-500/30"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Brain className="w-10 h-10 text-white" />
              </motion.div>
              <span className="text-white font-medium">AI Analysis</span>
              <span className="text-gray-500 text-sm">Root cause + fix</span>
            </div>

            {/* Arrow */}
            <motion.div
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
              className="hidden md:block"
            >
              <ArrowRight className="w-8 h-8 text-blue-500" />
            </motion.div>

            {/* Destinations */}
            <div className="flex flex-col items-center text-center">
              <div className="flex -space-x-3 mb-3">
                {[
                  { color: "#4A154B", icon: "S" },
                  { color: "#5865F2", icon: "D" },
                  { color: "#5059C9", icon: "T" },
                  { color: "#0088cc", icon: "Te" },
                ].map((dest, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm border-2 border-[#050505]"
                    style={{ backgroundColor: dest.color }}
                  >
                    {dest.icon}
                  </motion.div>
                ))}
              </div>
              <span className="text-white font-medium">Your Team</span>
              <span className="text-gray-500 text-sm">Instant delivery</span>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12"
        >
          <Link
            to="/register"
            className="inline-flex items-center gap-2 btn-primary px-8 py-4 rounded-lg text-white font-medium text-lg"
          >
            Connect Your Tools
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Mobile App Download Section
const MobileAppSection = () => {
  return (
    <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[100px]"></div>
        <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium mb-6">
              <Bell className="w-4 h-4" />
              Mobile Alerts
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Get alerted anywhere,<br />
              <span className="text-blue-400">anytime</span>
            </h2>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed">
              Never miss a critical alert. Our mobile apps deliver instant push notifications
              with AI-powered context, so you can acknowledge and resolve issues on the go.
            </p>

            <div className="space-y-4 mb-10">
              {[
                "Instant push notifications",
                "AI diagnosis in your pocket",
                "One-tap acknowledge & escalate",
                "Offline incident history",
                "Biometric security"
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-blue-400" />
                  </div>
                  <span className="text-gray-300">{feature}</span>
                </motion.div>
              ))}
            </div>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href="https://apps.apple.com/app/pagerless"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-white text-black hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="app-store-link"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs opacity-70">Download on the</div>
                  <div className="text-lg font-semibold -mt-1">App Store</div>
                </div>
              </motion.a>

              <motion.a
                href="https://play.google.com/store/apps/details?id=com.pagerless"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-white text-black hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="play-store-link"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs opacity-70">Get it on</div>
                  <div className="text-lg font-semibold -mt-1">Google Play</div>
                </div>
              </motion.a>
            </div>
          </motion.div>

          {/* Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative flex justify-center"
          >
            {/* Glow behind phone */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[400px] bg-blue-500/20 rounded-full blur-[80px]"></div>

            {/* Phone Frame */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <div className="relative w-[280px] h-[580px] bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] rounded-[3rem] p-2 shadow-2xl shadow-black/50 border border-white/10">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-black rounded-b-2xl z-20"></div>

                {/* Screen */}
                <div className="w-full h-full bg-[#0a0a0a] rounded-[2.5rem] overflow-hidden relative">
                  {/* Status Bar */}
                  <div className="flex justify-between items-center px-6 pt-3 pb-2 text-white text-xs">
                    <span>9:41</span>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-2 border border-white rounded-sm">
                        <div className="w-3/4 h-full bg-white rounded-sm"></div>
                      </div>
                    </div>
                  </div>

                  {/* App Content */}
                  <div className="px-4 pt-2">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                          <Bell className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-white font-semibold">Pagerless</span>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/10"></div>
                    </div>

                    {/* Alert Card */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="bg-gradient-to-br from-red-500/20 to-red-500/5 border border-red-500/30 rounded-2xl p-4 mb-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          <span className="text-red-400 text-xs font-medium">CRITICAL</span>
                        </div>
                        <span className="text-gray-500 text-xs">2m ago</span>
                      </div>
                      <div className="text-white text-sm font-medium mb-1">Database Connection Failed</div>
                      <div className="text-gray-400 text-xs mb-3">payment-api · prod-cluster</div>
                      <div className="flex gap-2">
                        <button className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-xs font-medium">
                          Acknowledge
                        </button>
                        <button className="flex-1 py-2 rounded-lg bg-white/10 text-white text-xs font-medium">
                          Escalate
                        </button>
                      </div>
                    </motion.div>

                    {/* AI Insight */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 }}
                      className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Brain className="w-4 h-4 text-blue-400" />
                        <span className="text-blue-400 text-xs font-medium">AI INSIGHT</span>
                      </div>
                      <div className="text-gray-300 text-xs leading-relaxed">
                        Connection pool exhausted. Recommended: Increase pool_size to 25.
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Floating notification */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8, x: 50 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 }}
                className="absolute -right-4 top-32 bg-[#1a1a1a] border border-white/10 rounded-xl p-3 shadow-xl max-w-[180px]"
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 rounded-lg bg-blue-600 flex items-center justify-center">
                    <Bell className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-white text-xs font-medium">Pagerless</span>
                </div>
                <div className="text-gray-400 text-xs">Alert resolved by AI auto-fix</div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Price Comparison Component - Horizontal Bars
const LegacyPriceComparison = () => {
  // Keep the pagerless bar simple: show only the AI portion ($8 / 10k alerts).
  const pagerlessAiValue = 8;

  const competitors = [
    { kind: "competitor", name: "VictorOps", price: 29, priceLabel: "$29/user", estimated: false },
    { kind: "competitor", name: "xMatters", price: 25, priceLabel: "$25/user", estimated: false },
    { kind: "competitor", name: "PagerDuty", price: 21, priceLabel: "$21/user", estimated: false },
    { kind: "competitor", name: "Zenduty", price: 24, priceLabel: "$24/user", estimated: true },
    { kind: "competitor", name: "Grafana OnCall (IM)", price: 18, priceLabel: "$18/user", estimated: true },
    {
      kind: "pagerless",
      name: "Pagerless",
      price: pagerlessAiValue,
      priceLabel: "$8",
      subLabel: "/10k alerts",
      segments: [
        { label: "AI Analysis", value: pagerlessAiValue, colorClass: "bg-blue-500/30" }
      ]
    },
  ];

  const sortedCompetitors = [...competitors].sort((a, b) => b.price - a.price);
  const maxPrice = Math.max(...sortedCompetitors.map((c) => c.price));

  return (
    <section className="py-24 md:py-32 bg-[#0A0A0A] overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
            The math is simple
          </h2>
          <p className="text-lg text-gray-400">
            They charge per user. We charge for what you use.
          </p>
        </motion.div>

        {/* Competitor bars */}
        <div className="space-y-4 mb-12">
          {sortedCompetitors.map((comp, i) => (
            <motion.div
              key={comp.name}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="relative"
            >
              <div className="flex items-center gap-4">
                <div className="w-36 text-right">
                  <span className="text-sm font-medium text-gray-400">
                    {comp.name}
                  </span>
                </div>
                <div className="flex-1 relative h-12">
                  <div className="absolute inset-0 rounded-lg bg-white/5"></div>
                  {comp.kind === "pagerless" ? (
                    <>
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${(comp.price / maxPrice) * 100}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.1, duration: 0.8, ease: "easeOut" }}
                        className="absolute inset-y-0 left-0 rounded-lg bg-blue-500/30"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <div className="flex flex-col items-end leading-tight">
                          <span className="text-lg font-bold text-blue-300">{comp.priceLabel}</span>
                          {comp.subLabel && (
                            <span className="text-[11px] text-gray-500 font-medium">{comp.subLabel}</span>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(comp.price / maxPrice) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1, duration: 0.8, ease: "easeOut" }}
                      className="absolute inset-y-0 left-0 rounded-lg flex items-center justify-end pr-4 bg-white/10"
                    >
                      <div className="flex flex-col items-end leading-tight">
                        <span className="text-lg font-bold text-gray-300">
                          {comp.priceLabel}
                        </span>
                        {comp.estimated && (
                          <span className="text-[11px] text-gray-500 font-medium">
                            *estimated
                          </span>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pagerless Pricing */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="grid md:grid-cols-2 gap-6"
        >
          <div className="p-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/30">
            <div className="flex items-center gap-3 mb-3">
              <Bell className="w-6 h-6 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Basic Paging</span>
            </div>
            <div className="text-4xl font-bold text-white mb-2">$1<span className="text-lg text-gray-400">/user/mo</span></div>
            <p className="text-gray-400 text-sm">Reliable alerting, on-call schedules, escalations, all integrations</p>
          </div>
          <div className="p-6 rounded-2xl bg-blue-500/10 border border-blue-500/30 relative overflow-hidden">
            <div className="absolute top-3 right-3">
              <span className="px-2 py-1 rounded-full bg-blue-500 text-white text-xs font-bold">PAY AS YOU GO</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <Brain className="w-6 h-6 text-blue-400" />
              <span className="text-blue-400 font-medium">AI Analysis</span>
            </div>
            <div className="text-4xl font-bold text-white mb-2">$8<span className="text-lg text-gray-400">/10k alerts</span></div>
            <p className="text-gray-400 text-sm">AI root cause analysis, auto-fix suggestions, 87% noise reduction</p>
          </div>
        </motion.div>

        {/* Example calculation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="mt-12 text-center"
        >
          <div className="inline-block px-8 py-6 rounded-2xl bg-[#050505] border border-white/10">
            <div className="text-sm text-gray-500 mb-2">Example: 50 users, 20k alerts/month</div>
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-gray-500 text-xs">PagerDuty</div>
                <div className="text-2xl font-bold text-red-400 line-through">$1,050/mo</div>
              </div>
              <div className="text-gray-600">vs</div>
              <div className="text-center">
                <div className="text-gray-500 text-xs">Pagerless</div>
                <div className="text-2xl font-bold text-emerald-400">$66/mo</div>
                <div className="text-xs text-gray-500">($50 + $16 AI)</div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 btn-primary px-8 py-4 rounded-lg text-white font-medium text-lg"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.9]);

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Analysis",
      description: "Our AI analyzes every alert, identifies root causes, and provides actionable debugging information automatically."
    },
    {
      icon: TrendingDown,
      title: "87% Alert Reduction",
      description: "Intelligent deduplication and correlation reduces alert noise by an average of 87%, letting you focus on what matters."
    },
    {
      icon: Clock,
      title: "Instant Resolution",
      description: "Get immediate insights on what's broken and how to fix it. No more digging through logs at 3 AM."
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "SOC2 compliant with end-to-end encryption. Your data stays yours."
    }
  ];

  const stats = [
    { value: "87%", label: "Alerts Reduced" },
    { value: "< 30s", label: "AI Analysis Time" },
    { value: "From $1", label: "Per User/Month" },
    { value: "99.9%", label: "Uptime SLA" }
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      <Navigation />

      {/* Hero Section with Parallax */}
      <section ref={heroRef} className="relative pt-32 pb-20 md:pt-40 md:pb-32 hero-gradient overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <FloatingElement delay={0} duration={4}>
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
          </FloatingElement>
          <FloatingElement delay={1} duration={5}>
            <div className="absolute top-40 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
          </FloatingElement>
          <FloatingElement delay={2} duration={6}>
            <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl"></div>
          </FloatingElement>

          {/* Floating Icons */}
          <motion.div
            animate={{ y: [0, -30, 0], x: [0, 20, 0], rotate: [0, 10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-32 right-[20%] opacity-20"
          >
            <Bell className="w-16 h-16 text-blue-400" />
          </motion.div>
          <motion.div
            animate={{ y: [0, 20, 0], x: [0, -15, 0], rotate: [0, -15, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute top-60 left-[15%] opacity-20"
          >
            <Brain className="w-12 h-12 text-purple-400" />
          </motion.div>
          <motion.div
            animate={{ y: [0, -25, 0], rotate: [0, 20, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute bottom-40 right-[10%] opacity-20"
          >
            <Zap className="w-10 h-10 text-yellow-400" />
          </motion.div>
        </div>

        <motion.div
          style={{ y: heroY, opacity: heroOpacity, scale: heroScale }}
          className="max-w-7xl mx-auto px-6 md:px-12 relative z-10"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6"
            >
              <Sparkles className="w-4 h-4" />
              AI-Powered Alert Management
            </motion.span>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-none text-white mb-6">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="block"
              >
                Sleep more,
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="gradient-text block"
              >
                Page less.
              </motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-lg md:text-xl text-gray-400 max-w-2xl mb-10 leading-relaxed"
            >
              Stop drowning in alert fatigue. Our AI reduces noise by 87%, automatically diagnoses issues,
              and tells you exactly what's wrong and how to fix it. Starting at just <span className="text-white font-semibold">$1/user/month</span>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                onClick={() => navigate(user ? "/dashboard" : "/register")}
                className="btn-primary px-8 py-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 text-lg group"
                data-testid="hero-cta"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Start Free Trial
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.div>
              </motion.button>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to="/features"
                  className="btn-secondary px-8 py-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 text-lg"
                  data-testid="hero-learn-more"
                >
                  See How It Works
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Stats with Parallax */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20"
          >
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                className="text-center md:text-left"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + i * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <motion.div
                  className="text-3xl md:text-4xl font-bold text-white font-['Space_Grotesk'] mb-1"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1 + i * 0.1, type: "spring" }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Apple-style Device Showcase */}
      <DeviceShowcase />

      {/* Price Comparison Section */}
      <PriceComparison />

      {/* Features Grid with Parallax */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 w-[800px] h-[800px] -translate-x-1/2 -translate-y-1/2 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ParallaxSection offset={40}>
            <div className="mb-16">
              <motion.h2
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-5xl font-bold text-white mb-4"
              >
                Why teams choose Pagerless
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
                className="text-lg text-gray-400 max-w-2xl"
              >
                Built for Indian startups who need enterprise-grade incident management without the enterprise price tag.
              </motion.p>
            </div>
          </ParallaxSection>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 50, rotateX: 20 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                whileHover={{
                  scale: 1.03,
                  y: -10,
                  boxShadow: "0 25px 50px -12px rgba(37, 99, 235, 0.25)"
                }}
                className="p-8 rounded-xl bg-[#0A0A0A] border border-white/10 card-hover group relative overflow-hidden"
              >
                {/* Hover glow effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                />

                <div className="relative z-10">
                  <motion.div
                    className="w-12 h-12 rounded-lg feature-icon flex items-center justify-center mb-6"
                    whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <feature.icon className="w-6 h-6 text-blue-400" />
                  </motion.div>
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-400 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Demo Section with 3D Effect */}
      <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ParallaxSection offset={60}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                  AI that actually helps
                </h2>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                  When an alert fires, our AI immediately analyzes logs, metrics, and traces to provide
                  a complete diagnosis. No more context switching or log diving.
                </p>

                <div className="space-y-4">
                  {[
                    "Automatic root cause analysis",
                    "Step-by-step remediation guides",
                    "Historical pattern recognition",
                    "Slack & Teams integration"
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <motion.div
                        className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center"
                        whileHover={{ scale: 1.2, backgroundColor: "rgba(37, 99, 235, 0.4)" }}
                      >
                        <Check className="w-3 h-3 text-blue-400" />
                      </motion.div>
                      <span className="text-gray-300">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </ParallaxSection>

            <motion.div
              initial={{ opacity: 0, x: 50, rotateY: 20 }}
              whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
              style={{ perspective: 1000 }}
            >
              <motion.div
                whileHover={{ rotateY: 5, rotateX: -5, scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="rounded-xl bg-[#151515] border border-white/10 p-6 font-mono text-sm relative overflow-hidden"
              >
                {/* Animated border */}
                <motion.div
                  className="absolute inset-0 rounded-xl"
                  style={{
                    background: "linear-gradient(90deg, transparent, rgba(37, 99, 235, 0.3), transparent)",
                    backgroundSize: "200% 100%",
                  }}
                  animate={{
                    backgroundPosition: ["200% 0", "-200% 0"],
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                />

                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4 text-gray-500">
                    <motion.div
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    >
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    </motion.div>
                    <span>Alert Analysis</span>
                    <motion.span
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="ml-auto text-green-400 text-xs"
                    >
                      ● Live
                    </motion.span>
                  </div>
                  <motion.div
                    className="space-y-3 text-gray-400"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={{
                      visible: { transition: { staggerChildren: 0.2 } }
                    }}
                  >
                    {[
                      { label: "Issue:", value: "High latency on /api/orders", color: "text-blue-400" },
                      { label: "Root Cause:", value: "Database connection pool exhausted", color: "text-blue-400" },
                      { label: "Impact:", value: "23% of requests affected", color: "text-blue-400" },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        variants={{
                          hidden: { opacity: 0, x: -20 },
                          visible: { opacity: 1, x: 0 }
                        }}
                      >
                        <span className={item.color}>{item.label}</span> {item.value}
                      </motion.div>
                    ))}
                    <motion.div
                      className="pt-2 border-t border-white/10"
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: { opacity: 1, y: 0 }
                      }}
                    >
                      <span className="text-green-400">Recommended Fix:</span>
                      <motion.div
                        className="mt-2 pl-4 border-l-2 border-green-500/30"
                        animate={{ borderColor: ["rgba(34, 197, 94, 0.3)", "rgba(34, 197, 94, 0.6)", "rgba(34, 197, 94, 0.3)"] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        Increase pool_size from 10 to 25 in database.yml
                      </motion.div>
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <IntegrationsSection />

      {/* Mobile App Section */}
      <MobileAppSection />

      {/* Pricing Preview with Animation */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ParallaxSection offset={30}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                Choose your plan
              </h2>
              <p className="text-lg text-gray-400 mb-12">
                Start with Basic or unlock AI superpowers
              </p>
            </motion.div>
          </ParallaxSection>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Basic Plan */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="rounded-2xl bg-[#0A0A0A] border border-white/10 p-8 text-left relative"
            >
              <div className="text-emerald-400 font-medium mb-4">Basic Paging</div>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-bold text-white font-['Space_Grotesk']">$1</span>
                <span className="text-gray-400">/user/month</span>
              </div>
              <p className="text-gray-400 mb-6">Perfect for teams who need reliable alerting without AI bells and whistles.</p>
              <ul className="space-y-3 mb-8">
                {[
                  "Unlimited alerts & incidents",
                  "On-call scheduling",
                  "Escalation policies",
                  "Slack, Teams, Discord",
                  "Mobile apps (iOS & Android)",
                  "Email & webhook integrations",
                  "Basic analytics"
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    className="flex items-center gap-3 text-gray-300 text-sm"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {item}
                  </motion.li>
                ))}
              </ul>
              <button
                onClick={() => window.location.href = '/register'}
                className="w-full py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
              >
                Start Free Trial
              </button>
            </motion.div>

            {/* AI Plan */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="rounded-2xl bg-gradient-to-b from-blue-500/10 to-transparent border border-blue-500/30 p-8 text-left relative pricing-spotlight"
            >
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-3 right-6"
              >
                <div className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  COMING SOON
                </div>
              </motion.div>

              <div className="text-blue-400 font-medium mb-4 flex items-center gap-2">
                <Brain className="w-4 h-4" />
                AI-Powered
              </div>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-bold text-white font-['Space_Grotesk']">$8</span>
                <span className="text-gray-400">/10k alerts</span>
              </div>
              <p className="text-gray-400 mb-6">For teams who want AI to reduce noise and accelerate resolution.</p>
              <ul className="space-y-3 mb-8">
                {[
                  "Everything in Basic, plus:",
                  "AI root cause analysis",
                  "Auto-fix recommendations",
                  "Intelligent deduplication",
                  "Pattern recognition",
                  "Priority support",
                  "Custom AI training"
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    className={`flex items-center gap-3 text-sm ${i === 0 ? 'text-blue-400 font-medium' : 'text-gray-300'}`}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Check className={`w-4 h-4 flex-shrink-0 ${i === 0 ? 'text-blue-400' : 'text-blue-400'}`} />
                    {item}
                  </motion.li>
                ))}
              </ul>
              {/* <motion.button
                onClick={() => window.location.href = '/register'}
                className="w-full py-3 rounded-lg text-white font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled
              >
                Coming Soon
              </motion.button> */}
            </motion.div>
          </div>

          <p className="text-sm text-gray-500 mt-8">14-day free trial on both plans. No credit card required.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white font-['Space_Grotesk']">Pagerless</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/features" className="hover:text-white transition-colors">Features</Link>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <div className="text-sm text-gray-500">
              © 2026 Pagerless.in. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Features Page
const FeaturesPage = () => {
  const features = [
    {
      icon: Brain,
      title: "AI-Powered Root Cause Analysis",
      description: "Our machine learning models analyze patterns across your entire stack to identify the true source of incidents, not just symptoms.",
      details: ["Correlates logs, metrics, and traces", "Learns from your infrastructure", "Reduces MTTR by 60%"]
    },
    {
      icon: TrendingDown,
      title: "Intelligent Alert Deduplication",
      description: "Stop getting paged for the same issue multiple times. Our AI groups related alerts and surfaces only what matters.",
      details: ["87% average noise reduction", "Smart grouping algorithms", "Customizable thresholds"]
    },
    {
      icon: Zap,
      title: "Automated Runbooks",
      description: "AI-generated remediation steps based on historical resolutions and best practices.",
      details: ["Step-by-step fix guides", "One-click automation", "Custom playbook support"]
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-grade security with SOC2 compliance, end-to-end encryption, and granular access controls.",
      details: ["SOC2 Type II certified", "Role-based access control", "Audit logging"]
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Built-in war rooms, incident timelines, and post-mortem tools keep your team in sync.",
      details: ["Real-time collaboration", "Slack & Teams native", "Incident timeline"]
    },
    {
      icon: BarChart3,
      title: "Analytics & Insights",
      description: "Track MTTD, MTTR, and alert trends. Identify reliability improvements over time.",
      details: ["Custom dashboards", "SLA tracking", "Trend analysis"]
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navigation />

      <section className="pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Features built for modern DevOps
            </h1>
            <p className="text-lg text-gray-400">
              Everything you need to manage incidents effectively, powered by AI that actually understands your systems.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-8 rounded-xl bg-[#0A0A0A] border border-white/10 card-hover"
              >
                <div className="w-12 h-12 rounded-lg feature-icon flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.details.map((detail, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-500">
                      <Check className="w-4 h-4 text-blue-400" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Pricing Page
const PricingPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      q: "What's the difference between Basic and AI plans?",
      a: "Basic includes all core paging features. AI adds intelligent root cause analysis, auto-fix recommendations, and 87% noise reduction through smart deduplication."
    },
    {
      q: "Is there a free trial?",
      a: "Yes! You get a 14-day free trial with full access to the AI plan features. No credit card required."
    },
    {
      q: "What happens after 500 users?",
      a: "Contact us for custom enterprise pricing. We offer volume discounts for larger teams."
    },
    {
      q: "Can I switch between plans?",
      a: "Absolutely. Upgrade or downgrade anytime. Changes take effect on your next billing cycle."
    },
    {
      q: "How does billing work?",
      a: "We bill monthly based on active users. You only pay for team members who actually use Pagerless."
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navigation />

      <section className="pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Simple, transparent pricing
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Choose Basic for reliable alerting, or unlock AI superpowers for faster resolution.
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-20">
            {/* Basic Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl bg-[#0A0A0A] border border-white/10 p-8"
            >
              <div className="flex items-center gap-2 mb-4">
                <Bell className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-400 font-medium">Basic Paging</span>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-6xl font-bold text-white font-['Space_Grotesk']">$1</span>
                <span className="text-xl text-gray-400">/user/month</span>
              </div>
              <p className="text-gray-500 mb-8">Reliable alerting for teams up to 500 users</p>

              <div className="space-y-4 mb-8">
                {[
                  "Unlimited alerts & incidents",
                  "On-call scheduling & rotations",
                  "Escalation policies",
                  "Slack, Teams, Discord integration",
                  "Mobile apps (iOS & Android)",
                  "Email & webhook notifications",
                  "Basic analytics & reporting",
                  "99.9% uptime SLA",
                  "Email support"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate("/register")}
                className="w-full py-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
              >
                Start Free Trial
              </button>
            </motion.div>

            {/* AI Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl bg-gradient-to-b from-blue-500/10 to-transparent border border-blue-500/30 p-8 relative pricing-spotlight"
            >
              <div className="absolute -top-3 right-6 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                Coming Soon
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-5 h-5 text-blue-400" />
                <span className="text-blue-400 font-medium">AI-Powered</span>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-6xl font-bold text-white font-['Space_Grotesk']">$8</span>
                <span className="text-xl text-gray-400">/user/month</span>
              </div>
              <p className="text-gray-500 mb-8">AI-powered incident management for faster resolution</p>

              <div className="space-y-4 mb-8">
                {[
                  { text: "Everything in Basic, plus:", highlight: true },
                  { text: "AI root cause analysis" },
                  { text: "Auto-fix recommendations" },
                  { text: "Intelligent deduplication" },
                  { text: "Pattern recognition & learning" },
                  { text: "Predictive alerting" },
                  { text: "Custom AI model training" },
                  { text: "Priority support (4hr SLA)" }
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-3 ${item.highlight ? 'text-blue-400 font-medium' : 'text-gray-300'}`}>
                    <Check className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <span>{typeof item === 'string' ? item : item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Enterprise */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-2xl mx-auto mb-20"
          >
            <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Enterprise</h3>
                <p className="text-gray-400">Custom solutions for teams larger than 500 users with dedicated support.</p>
              </div>
              <Link
                to="/contact"
                className="btn-secondary px-8 py-3 rounded-lg text-white font-medium flex items-center gap-2 whitespace-nowrap"
              >
                Contact Sales
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* FAQs */}
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="p-6 rounded-xl bg-[#0A0A0A] border border-white/10"
                >
                  <h4 className="font-medium text-white mb-2">{faq.q}</h4>
                  <p className="text-gray-400 text-sm">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-12">14-day free trial on both plans. No credit card required.</p>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navigation />

      <section className="pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Get in touch
              </h1>
              <p className="text-lg text-gray-400 mb-8">
                Have questions? Want a demo? We'd love to hear from you.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-1">Email</h4>
                    <p className="text-gray-400">hello@pagerless.in</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-1">Response Time</h4>
                    <p className="text-gray-400">We typically respond within 24 hours</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full input-glass px-4 py-3 rounded-lg text-white"
                    placeholder="Your name"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full input-glass px-4 py-3 rounded-lg text-white"
                    placeholder="you@company.com"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Company (optional)</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full input-glass px-4 py-3 rounded-lg text-white"
                    placeholder="Your company"
                    data-testid="contact-company"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full input-glass px-4 py-3 rounded-lg text-white resize-none"
                    placeholder="How can we help?"
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-4 rounded-lg text-white font-medium disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-white font-['Space_Grotesk']">Pagerless</span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome back</h1>
          <p className="text-gray-400">Sign in to your account</p>
        </div>

        <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-8">
          <button
            onClick={handleGoogleLogin}
            className="w-full btn-secondary py-3 rounded-lg text-white font-medium flex items-center justify-center gap-3 mb-6"
            data-testid="google-login"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0A0A0A] text-gray-500">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full input-glass px-4 py-3 rounded-lg text-white"
                placeholder="you@company.com"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full input-glass px-4 py-3 rounded-lg text-white"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 rounded-lg text-white font-medium disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="text-center text-gray-400 text-sm mt-6">
            Don't have an account?{" "}
            <Link to="/register" className="text-blue-400 hover:text-blue-300">
              Sign up
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", password: "", company_name: "" });
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      toast.success("Account created! Let's get you set up.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-2xl text-white font-['Space_Grotesk']">Pagerless</span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">Start your free trial</h1>
          <p className="text-gray-400">14 days free. No credit card required.</p>
        </div>

        <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-8">
          <button
            onClick={handleGoogleLogin}
            className="w-full btn-secondary py-3 rounded-lg text-white font-medium flex items-center justify-center gap-3 mb-6"
            data-testid="google-register"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0A0A0A] text-gray-500">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full input-glass px-4 py-3 rounded-lg text-white"
                placeholder="John Doe"
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Work Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full input-glass px-4 py-3 rounded-lg text-white"
                placeholder="you@company.com"
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full input-glass px-4 py-3 rounded-lg text-white"
                placeholder="••••••••"
                data-testid="register-password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Company (optional)</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="w-full input-glass px-4 py-3 rounded-lg text-white"
                placeholder="Acme Inc"
                data-testid="register-company"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 rounded-lg text-white font-medium disabled:opacity-50"
              data-testid="register-submit"
            >
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>

          <p className="text-center text-gray-400 text-sm mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-400 hover:text-blue-300">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Auth Callback
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { updateUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/google/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );

          updateUser(response.data.user);
          toast.success("Welcome to Pagerless!");
          navigate("/dashboard", { replace: true, state: { user: response.data.user } });
        } catch (error) {
          console.error("Auth error:", error);
          toast.error("Authentication failed. Please try again.");
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, updateUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 spinner mx-auto mb-4"></div>
        <p className="text-gray-400">Completing sign in...</p>
      </div>
    </div>
  );
};

// Dashboard
const Dashboard = () => {
  const { user, logout, updateUser, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingData, setOnboardingData] = useState({
    company_name: "",
    team_size: "",
    current_tools: "",
    primary_use_case: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      // Use user from location state if available (from AuthCallback)
      const currentUser = location.state?.user || user;

      if (!currentUser) {
        navigate("/login");
        return;
      }

      if (!currentUser.onboarding_completed) {
        setShowOnboarding(true);
        setLoading(false);
        return;
      }

      try {
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await axios.get(`${API}/dashboard/stats`, {
          headers,
          withCredentials: true
        });
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, token, navigate, location.state]);

  const handleOnboardingSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      await axios.post(`${API}/onboarding`, onboardingData, {
        headers,
        withCredentials: true
      });

      updateUser({ ...user, onboarding_completed: true, company_name: onboardingData.company_name });
      setShowOnboarding(false);
      toast.success("Onboarding complete! Welcome to Pagerless.");

      // Fetch stats after onboarding
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers,
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to complete onboarding");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const currentUser = location.state?.user || user;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 spinner"></div>
      </div>
    );
  }

  if (showOnboarding) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg"
        >
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Let's get you set up</h1>
            <p className="text-gray-400">Tell us a bit about your team</p>
          </div>

          <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-8">
            <form onSubmit={handleOnboardingSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Company Name</label>
                <input
                  type="text"
                  required
                  value={onboardingData.company_name}
                  onChange={(e) => setOnboardingData({ ...onboardingData, company_name: e.target.value })}
                  className="w-full input-glass px-4 py-3 rounded-lg text-white"
                  placeholder="Acme Inc"
                  data-testid="onboarding-company"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Team Size</label>
                <select
                  required
                  value={onboardingData.team_size}
                  onChange={(e) => setOnboardingData({ ...onboardingData, team_size: e.target.value })}
                  className="w-full input-glass px-4 py-3 rounded-lg text-white bg-transparent"
                  data-testid="onboarding-team-size"
                >
                  <option value="" className="bg-[#0A0A0A]">Select team size</option>
                  <option value="1-10" className="bg-[#0A0A0A]">1-10</option>
                  <option value="11-50" className="bg-[#0A0A0A]">11-50</option>
                  <option value="51-200" className="bg-[#0A0A0A]">51-200</option>
                  <option value="201-500" className="bg-[#0A0A0A]">201-500</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Current Monitoring Tools (optional)</label>
                <input
                  type="text"
                  value={onboardingData.current_tools}
                  onChange={(e) => setOnboardingData({ ...onboardingData, current_tools: e.target.value })}
                  className="w-full input-glass px-4 py-3 rounded-lg text-white"
                  placeholder="e.g., Datadog, PagerDuty, Grafana"
                  data-testid="onboarding-tools"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Primary Use Case (optional)</label>
                <select
                  value={onboardingData.primary_use_case}
                  onChange={(e) => setOnboardingData({ ...onboardingData, primary_use_case: e.target.value })}
                  className="w-full input-glass px-4 py-3 rounded-lg text-white bg-transparent"
                  data-testid="onboarding-use-case"
                >
                  <option value="" className="bg-[#0A0A0A]">Select use case</option>
                  <option value="reduce-alerts" className="bg-[#0A0A0A]">Reduce alert noise</option>
                  <option value="faster-resolution" className="bg-[#0A0A0A]">Faster incident resolution</option>
                  <option value="cost-savings" className="bg-[#0A0A0A]">Cost savings</option>
                  <option value="all" className="bg-[#0A0A0A]">All of the above</option>
                </select>
              </div>
              <button
                type="submit"
                className="w-full btn-primary py-4 rounded-lg text-white font-medium"
                data-testid="onboarding-submit"
              >
                Complete Setup
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="dashboard">
      {/* Top Nav */}
      <header className="fixed top-0 left-0 right-0 z-50 nav-blur border-b border-white/5">
        <div className="flex items-center justify-between h-16 px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-white font-['Space_Grotesk']">Pagerless</span>
          </Link>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400 hidden sm:block">{currentUser?.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="dashboard-logout"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:block text-sm">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome back, {currentUser?.name?.split(' ')[0]}!
            </h1>
            <p className="text-gray-400 mb-8">Here's how Pagerless is helping your team.</p>
          </motion.div>

          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Alerts Reduced", value: `${stats.alerts_reduced}%`, icon: TrendingDown, color: "text-green-400" },
                { label: "Hours Saved", value: stats.time_saved_hours, icon: Clock, color: "text-blue-400" },
                { label: "Monthly Cost", value: `$${stats.monthly_cost.toFixed(2)}`, icon: DollarSign, color: "text-yellow-400" },
                { label: "AI Resolved", value: stats.ai_resolved, icon: Brain, color: "text-purple-400" }
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="stat-card rounded-xl p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    <span className="text-sm text-gray-500">{stat.label}</span>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-white font-['Space_Grotesk']">
                    {stat.value}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-xl bg-[#0A0A0A] border border-white/10 p-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <button className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-blue-500/50 transition-colors text-left">
                <Settings className="w-5 h-5 text-blue-400 mb-2" />
                <div className="text-white font-medium">Integrations</div>
                <div className="text-sm text-gray-500">Connect your tools</div>
              </button>
              <button className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-blue-500/50 transition-colors text-left">
                <Users className="w-5 h-5 text-blue-400 mb-2" />
                <div className="text-white font-medium">Team</div>
                <div className="text-sm text-gray-500">Manage members</div>
              </button>
              <button className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-blue-500/50 transition-colors text-left">
                <BarChart3 className="w-5 h-5 text-blue-400 mb-2" />
                <div className="text-white font-medium">Analytics</div>
                <div className="text-sm text-gray-500">View reports</div>
              </button>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 spinner"></div>
      </div>
    );
  }

  // If user passed from AuthCallback via location.state, allow access
  if (location.state?.user) {
    return children;
  }

  if (!user) {
    return <LoginPage />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#0A0A0A',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#EDEDED'
            }
          }}
        />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
